import React, { useState, useRef, useEffect } from 'react';
import './Skills.css';

interface Skill {
  name: string;
  icon: string;
  category: string;
}

interface Category {
  id: string;
  name: string;
}

function Skills(): JSX.Element {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [animateSkills, setAnimateSkills] = useState<boolean>(false);
  const skillsRef = useRef<HTMLElement>(null);

  const skillsData: Skill[] = [
    { name: 'Linux', icon: 'skills/linux-logo.png', category: 'os' },
    { name: 'BASH', icon: 'skills/bash-logo.jpg', category: 'programming' },
    { name: 'Python', icon: 'skills/python-logo.png', category: 'programming' },
    { name: 'C++', icon: 'skills/cpp-logo.png', category: 'programming' },
    { name: 'Git', icon: 'skills/git-logo.png', category: 'tools' },
    { name: 'Docker', icon: 'skills/docker-logo.png', category: 'container' },
    { name: 'Kubernetes', icon: 'skills/k8s-logo.png', category: 'container' },
    { name: 'OpenShift', icon: 'skills/ocp-logo.png', category: 'container' },
    { name: 'Helm', icon: 'skills/helm-logo.png', category: 'container' },
    { name: 'Artifactory', icon: 'skills/artifactory-logo.png', category: 'cicd' },
    { name: 'Jenkins', icon: 'skills/jenkins-logo.png', category: 'cicd' },
    { name: 'Ansible', icon: 'skills/ansible-logo.png', category: 'automation' },
    { name: 'Elasticsearch', icon: 'skills/elasticsearch-logo.png', category: 'monitoring' },
    { name: 'Prometheus', icon: 'skills/prometheus-logo.png', category: 'monitoring' },
    { name: 'Grafana', icon: 'skills/grafana-logo.png', category: 'monitoring' },
    { name: 'AWS', icon: 'skills/aws-logo-dark.png', category: 'cloud' },
    { name: 'Terraform', icon: 'skills/terraform-logo.png', category: 'iac' },
  ];

  const categories: Category[] = [
    { id: 'all', name: 'All Skills' },
    { id: 'programming', name: 'Programming' },
    { id: 'container', name: 'Containerization' },
    { id: 'cicd', name: 'CI/CD' },
    { id: 'monitoring', name: 'Monitoring' },
    { id: 'cloud', name: 'Cloud' },
    { id: 'automation', name: 'Automation' },
    { id: 'iac', name: 'Infrastructure as Code' },
    { id: 'os', name: 'Operating Systems' },
    { id: 'tools', name: 'Tools' },
  ];

  const filteredSkills: Skill[] = activeCategory === 'all'
    ? skillsData
    : skillsData.filter(skill => skill.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimateSkills(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    const current = skillsRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return (
    <section className="Skills" ref={skillsRef}>
      <div className="section-header">
        <h2 className="section-title">Skills & Technologies</h2>
        <div className="section-subtitle">Technical proficiencies I've developed over the years</div>
      </div>

      <div className="skill-categories">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-button ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      <div className={`skill-list ${animateSkills ? 'animate' : ''}`}>
        {filteredSkills.map((skill, index) => (
          <div
            className="skill"
            key={index}
            style={{ animationDelay: `${0.05 * index}s` }}
          >
            <div className="skill-icon">
              <img src={skill.icon} alt={skill.name} />
            </div>
            <div className="skill-name">{skill.name}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Skills;
